import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const ManualEntry = ({ onSessionCreated }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '10:00',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
      const endDateTime = new Date(`${formData.date}T${formData.endTime}`);

      if (endDateTime <= startDateTime) {
        toast.error('End time must be after start time');
        setIsSubmitting(false);
        return;
      }

      const durationMinutes = Math.floor((endDateTime - startDateTime) / 1000 / 60);

      await axios.post(`${API}/sessions`, {
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        duration_minutes: durationMinutes,
        session_type: 'manual',
        notes: formData.notes || null
      });

      toast.success(`Session added! ${durationMinutes} minutes logged.`);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        startTime: '09:00',
        endTime: '10:00',
        notes: ''
      });
      onSessionCreated();
    } catch (error) {
      toast.error('Failed to add session');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="backdrop-blur-sm bg-white/90 border-emerald-200 shadow-xl" data-testid="manual-entry-card">
      <CardHeader>
        <CardTitle className="text-2xl text-emerald-900">Manual Entry</CardTitle>
        <CardDescription className="text-emerald-700">Log past outdoor sessions</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="date" className="text-emerald-900">Date</Label>
            <Input
              id="date"
              data-testid="manual-date-input"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              required
              className="border-emerald-200 focus:border-emerald-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime" className="text-emerald-900">Start Time</Label>
              <Input
                id="startTime"
                data-testid="manual-start-time-input"
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                required
                className="border-emerald-200 focus:border-emerald-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endTime" className="text-emerald-900">End Time</Label>
              <Input
                id="endTime"
                data-testid="manual-end-time-input"
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                required
                className="border-emerald-200 focus:border-emerald-400"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-emerald-900">Notes (Optional)</Label>
            <Textarea
              id="notes"
              data-testid="manual-notes-input"
              placeholder="Where did you go? What did you do?"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="border-emerald-200 focus:border-emerald-400 min-h-[100px]"
            />
          </div>

          <Button
            type="submit"
            data-testid="manual-submit-btn"
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white h-12 shadow-lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding Session...' : 'Add Session'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ManualEntry;